import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";

/**
 * A yes/no dialog, in the application's own styling.
 *
 * Replaces `window.confirm`. The browser's own dialog is a poor fit for the
 * decisions it was guarding here — deleting a branch, turning down a customer's
 * sale, clearing somebody's limit. It cannot be styled, cannot mark a
 * destructive action as destructive, is suppressible by the browser, and on
 * mobile appears as a system alert that names the site rather than the action.
 *
 * The hook is promise-based so call sites keep the shape they already had:
 *
 *     if (!(await confirm({ title: "...", message: "..." }))) return;
 *
 * which is the same sentence `window.confirm` produced, and means replacing it
 * cannot quietly change a flow's control flow.
 */

export interface ConfirmOptions {
    title: string;
    message: string;
    /** Defaults to "Confirm". */
    confirmLabel?: string;
    cancelLabel?: string;
    /** `danger` for anything that destroys or refuses something. */
    tone?: "default" | "danger";
}

type Resolver = (answer: boolean) => void;

const ConfirmContext = createContext<(options: ConfirmOptions) => Promise<boolean>>(
    async () => false,
);

/** Ask the question. Resolves true only if the person confirmed. */
export function useConfirm() {
    return useContext(ConfirmContext);
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    const resolverRef = useRef<Resolver | null>(null);
    const confirmButtonRef = useRef<HTMLButtonElement>(null);

    const confirm = useCallback((next: ConfirmOptions) => {
        setOptions(next);
        return new Promise<boolean>((resolve) => {
            resolverRef.current = resolve;
        });
    }, []);

    const answer = useCallback((value: boolean) => {
        // Resolve before clearing, and clear the ref either way: a dialog that
        // resolved twice, or never, would leave the caller's await hanging.
        resolverRef.current?.(value);
        resolverRef.current = null;
        setOptions(null);
    }, []);

    // Escape cancels, Enter confirms — what the browser dialog did, and what
    // anybody who has used one expects.
    useEffect(() => {
        if (!options) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") answer(false);
            if (e.key === "Enter") answer(true);
        };
        document.addEventListener("keydown", onKey);
        confirmButtonRef.current?.focus();
        return () => document.removeEventListener("keydown", onKey);
    }, [options, answer]);

    const danger = options?.tone === "danger";

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            {options && (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
                    // A click on the backdrop is a cancel, never a confirm.
                    onClick={() => answer(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {danger && (
                            <div className="mb-3 flex justify-center">
                                <FaExclamationTriangle className="text-red-500" size={26} />
                            </div>
                        )}

                        <h2 className={`mb-2 text-center text-lg font-bold ${danger ? "text-red-700" : "text-primary"}`}>
                            {options.title}
                        </h2>
                        <p className="mb-6 text-center text-sm text-gray-600">{options.message}</p>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => answer(false)}
                                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                            >
                                {options.cancelLabel ?? "Cancel"}
                            </button>
                            <button
                                ref={confirmButtonRef}
                                type="button"
                                onClick={() => answer(true)}
                                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold text-white transition ${
                                    danger ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-blue-800"
                                }`}
                            >
                                {options.confirmLabel ?? "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
};

export default ConfirmProvider;
